import {skipToken} from '@reduxjs/toolkit/query';
import {match, Pattern} from 'ts-pattern';

import {always, isNil, isNotNil} from 'ramda';

import {useGetTenantV2Query} from '@dms/api/base';
import {
  useGetConsentListQuery,
  useGetCurrentUserInfoQuery,
  useGetCustomerV2Query,
} from '@dms/api/customer';
import {useListPermissionExpressionQuery} from '@dms/api/security';
import {usePermissions} from '@dms/shared';

import {Nullish} from 'shared/web';

import {DefaultSignatoriesFormData} from '../../../types/DefaultSignatoriesFormData';
import {DocumentDefaultSignatoriesFormData} from '../../../types/DocumentDefaultSignatoriesFormData';
import {getContractInformationSignatoryDefaults} from '../../../utils/getContractInformationSignatoryDefaults';
import {getCurrentUserSignatoryDefaults} from '../../../utils/getCurrentUserSignatoryDefaults';
import {getCustomerSignatoryDefaults} from '../../../utils/getCustomerSignatoryDefaults';

/**
 * Loads and derives the default signatory values (customer and current user)
 * used to pre-fill all documents in the signatories step. A consent document given by a contact
 * person of the customer gets that contact person as its customer signatory, and one given without
 * a contact person gets the signatory of the billing info it was given on. A failed consent
 * lookup is an error rather than a fallback to another person. Users who cannot read consents
 * get the customer defaults.
 */
export const useDefaultSignatoriesData = (customerId: string | Nullish) => {
  const {
    data: permissions,
    isLoading: isPermissionsLoading,
    isError: isPermissionsError,
  } = useListPermissionExpressionQuery();
  const [canReadConsents] = usePermissions({
    permissionKeys: ['readGdprConsentDocument'],
    scopes: {
      readGdprConsentDocument: {
        // Customer has no branchId
        branchId: 'SKIP',
      },
    },
  });

  const {data: currentUser, isLoading: isCurrentUserLoading} = useGetCurrentUserInfoQuery();
  const {data: tenant, isLoading: isTenantLoading} = useGetTenantV2Query();
  const {data: customer, isLoading: isCustomerLoading} = useGetCustomerV2Query(
    isNotNil(customerId) ? {customerId} : skipToken
  );
  const {
    data: consents,
    isLoading: isConsentListLoading,
    isError: isConsentListError,
  } = useGetConsentListQuery(isNotNil(customerId) && canReadConsents ? {customerId} : skipToken);

  const customerDefaults = getCustomerSignatoryDefaults(customer, tenant);
  const currentUserDefaults = getCurrentUserSignatoryDefaults(currentUser, tenant);

  const defaultSignatoriesData: DefaultSignatoriesFormData = {
    ...(isNotNil(customerDefaults) ? {customer: customerDefaults} : {}),
    ...(isNotNil(currentUserDefaults) ? {tenant: currentUserDefaults} : {}),
  };

  const documentDefaultSignatoriesData: DocumentDefaultSignatoriesFormData = Object.fromEntries(
    (consents ?? []).flatMap((consent) => {
      const consentDefaults = match(consent)
        .with({contactPersonId: Pattern.string}, ({contactPersonId}) =>
          getCustomerSignatoryDefaults(customer, tenant, contactPersonId)
        )
        .with({customerContractInformationId: Pattern.string}, ({customerContractInformationId}) =>
          getContractInformationSignatoryDefaults(customer, customerContractInformationId)
        )
        .otherwise(always(null));

      return isNil(consentDefaults) ? [] : [[consent.fileId, {customer: consentDefaults}] as const];
    })
  );

  return {
    defaultSignatoriesData,
    documentDefaultSignatoriesData,
    isLoading:
      isPermissionsLoading ||
      isCurrentUserLoading ||
      isTenantLoading ||
      isCustomerLoading ||
      isConsentListLoading,
    // Without loaded permissions, a user who can read consents looks like one who cannot.
    // A failed refetch keeps the cached permissions, which are still valid.
    isError: (isPermissionsError && isNil(permissions)) || isConsentListError,
  };
};
